import { createApi } from '@reduxjs/toolkit/query/react';
import { filteredFilter } from '../../utils/api';
import {properties} from "../../properties.js";
import { authBaseQuery } from '../../app/axiosBaseQuery';
import { PaginationResult, ListRequest } from '../../types/global.d';
import * as Entity from '../types/models/Address';
import {createNormalizationMiddleware} from "../../app/normy/normyRtkQuery.ts";

export const ADDRESS_API_REDUCER_KEY = 'addresses';
export const ADDRESS_PATH = 'addresses';

export const addressApi = createApi({
    reducerPath: ADDRESS_API_REDUCER_KEY,
    baseQuery: authBaseQuery(),
    keepUnusedDataFor: 10,
    refetchOnMountOrArgChange: 10,
    refetchOnFocus: true,
    tagTypes: ['Address', 'Addresses'],
    endpoints: (builder) => ({
        getAddressList: builder.query<PaginationResult<Entity.Address>, ListRequest>({
            query: (request) => {
                const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request))
                return ({
                    url: `/${ ADDRESS_PATH }?${new URLSearchParams(queryParams).toString()}`,
                    method: 'GET',
                })
            },
            providesTags: ['Addresses'],
        }),
        getAddress: builder.query<Entity.Address, Entity.PrimaryAddress>({
            queryFn: async (id, { getState }, extraOptions, baseQuery) => {

                const cachedData = addressNormalizer.getObjectById(id.id);

                if (cachedData) {
                    return { data: cachedData };
                } else {
                    const response = await baseQuery({
                        url: `/${ ADDRESS_PATH }/${id.toPath()}`,
                        method: 'GET',
                    });

                    return response;
                }
            },
            providesTags: ['Address'],
        }),
        getAddressMultiple: builder.query<Entity.Address, Entity.PrimaryAddress[]>({
            query: (ids) => {
                return ({
                    url: `/${ ADDRESS_PATH }/ids${Entity.PrimaryAddress.toQueryParams(ids)}`,
                    method: 'GET',
                })
            },
            providesTags: ['Addresses'],
        }),
        getAddressPresentation: builder.query<PaginationResult<Entity.Address>, Entity.PresentationAddressRequest>({
            query: (request) => {
              const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request.list))
              return ({
                url: `/${ ADDRESS_PATH }/presentation?${new URLSearchParams(queryParams).toString()}`,
                method: 'POST',
                data: request.filter,
              })
            },
            providesTags: ['Addresses'],
        }),
        
        insertAddress: builder.mutation<Entity.Address, Entity.CreateAddress>({
            query: (toInsert) => {
                return ({
                    url: `/${ ADDRESS_PATH }`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                dispatch(
                    addressApi.util.upsertQueryData('getAddress', Entity.PrimaryAddress.fromId(mutationData.id), mutationData),
                );
            },
            invalidatesTags: ['Addresses'],
        }),
        insertMultipleAddress: builder.mutation<Entity.Address[], Entity.CreateAddress[]>({
            query: (toInsert) => {
                return ({
                    url: `/${ ADDRESS_PATH }/batch`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                for(const item of mutationData) {
                    dispatch(
                            addressApi.util.upsertQueryData('getAddress', Entity.PrimaryAddress.fromId(item.id), item),
                    );
                }
            },
            invalidatesTags: ['Addresses'],
        }),
        updateAddress: builder.mutation<Entity.Address, Entity.UpdateEntityAddress>({
            query: (toUpdate) => {
                return ({
                    url: `/${ ADDRESS_PATH }/${toUpdate.id.toPath()}`,
                    method: 'PUT',
                    data: toUpdate.data,
                })
            },
            invalidatesTags: ['Addresses'],
        }),
        updateMultipleAddress: builder.mutation<Entity.Address[], Entity.UpdateEntityAddress[]>({
            query: (toUpdate) => {
                return ({
                    url: `/${ ADDRESS_PATH }/batch`,
                    method: 'PUT',
                    data: toUpdate,
                })
            },
            invalidatesTags: ['Addresses'],
        }),
        deleteAddress: builder.mutation<Entity.Address, Entity.Address>({
            query: (toDelete) => {
                return ({
                    url: `/${ ADDRESS_PATH }/${Entity.PrimaryAddress.fromEntity(toDelete)?.toPath()}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['Addresses'],
        }),
        deleteMultipleAddress: builder.mutation<Entity.Address[], Entity.PrimaryAddress[]>({
            query: (ids) => {
                return ({
                    url: `/${ ADDRESS_PATH }/batch${Entity.PrimaryAddress.toQueryParams(ids)}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['Addresses'],
        }),
    }),
})

export const [addressNormalizer, addressNormalizerMiddleware] = createNormalizationMiddleware(ADDRESS_API_REDUCER_KEY, addressApi, {
    devLogging: properties.debugLog,
})

export const {
    useGetAddressListQuery,
    useGetAddressMultipleQuery,
    useGetAddressPresentationQuery,
    useGetAddressQuery,
    useInsertAddressMutation,
    useInsertMultipleAddressMutation,
    useUpdateAddressMutation,
    useUpdateMultipleAddressMutation,
    useDeleteAddressMutation,
    useDeleteMultipleAddressMutation,
    
} = addressApi
